import type { ObjectSchema, Schema } from '@hapi/joi';

import type { DetailsRequired, SigninRequirements } from '@sine/sine-core-api-types';
import { type ISelectOption } from '@sine/sine-ui-components';

import Joi, { MAX_CHARACTERS, uuidv4 } from '../../../helpers/joiCommonSchemas';
import { validEmail } from '../../../helpers/joiCommonSchemas';
import { type ISelectOptionVisitorTypesRequirements } from '../../../helpers/mapToDropdownOptions';
import {
  singleSelectDropdownOption,
  singleSelectDropdownOptionWithVtRequirements,
} from '../../../shared/schemas/dropdown';
import { type SiteOptionWithCountryCode } from '../SigninProvider';

export interface VisitorDetailsForm {
  firstName: string;
  lastName: string;
  company?: string;
  companyId?: string;
  companySource?: string;
  site: Array<SiteOptionWithCountryCode>;
  visitorType: Array<ISelectOptionVisitorTypesRequirements>;
  host: Array<ISelectOption>;
  zone: Array<ISelectOption>;
}

export const VALIDATION_MESSAGE = {
  FIRST_NAME_MISSING: 'Please enter your first name',
  FIRST_NAME_MIN_LENGTH: 'First name is too short',
  FIRST_NAME_MAX_LENGTH: 'First name is too long',

  LAST_NAME_MISSING: 'Please enter your last name',
  LAST_NAME_MIN_LENGTH: 'Last name is too short',
  LAST_NAME_MAX_LENGTH: 'Last name is too long',

  EMAIL_MISSING: 'Please enter a valid email',

  MOBILE_MISSING: 'Please enter a valid mobile number',
  MOBILE_INCORRECT: 'Please enter a valid mobile number',

  EMAIL_OR_MOBILE_REQUIRED: 'Email or mobile required',

  COMPANY_MISSING: 'Please enter your company name',
  COMPANY_MIN_LENGTH: 'Company name is too short',
  COMPANY_MAX_LENGTH: 'Company name is too long',
};

const firstNameSchema = () =>
  Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.FIRST_NAME_MISSING,
    'string.min': VALIDATION_MESSAGE.FIRST_NAME_MIN_LENGTH,
    'string.max': VALIDATION_MESSAGE.FIRST_NAME_MAX_LENGTH,
  });

const lastNameSchema = () =>
  Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.LAST_NAME_MISSING,
    'string.min': VALIDATION_MESSAGE.LAST_NAME_MIN_LENGTH,
    'string.max': VALIDATION_MESSAGE.LAST_NAME_MAX_LENGTH,
  });

const companySchema = () =>
  Joi.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.COMPANY_MISSING,
    'string.max': VALIDATION_MESSAGE.COMPANY_MAX_LENGTH,
  });

const companyIdSchema = () => uuidv4().optional().allow(null);

const companySourceSchema = () =>
  Joi.string().trim().valid('new', 'global', 'verified').allow(null).messages({
    'any.only': 'Value must be one of ["new", "global", "verified"]',
  });

const mobileSchema = () => Joi.string().phoneNumber();

function optionalField(schema: Schema) {
  return schema.allow('').optional();
}

function isEmailOrMobileRequired(schema: ObjectSchema) {
  return schema.append({
    // Email is optional, but if provided it must be valid
    email: Joi.alternatives().conditional('mobile', {
      is: mobileSchema(),
      then: optionalField(validEmail()),
      otherwise: validEmail().required().messages({
        'any.required': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
        'string.empty': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
      }),
    }),

    // If email is valid, mobile becomes optional; otherwise mobile is required
    mobile: Joi.alternatives().conditional('email', {
      is: validEmail(),
      then: optionalField(mobileSchema()),
      otherwise: mobileSchema().required().messages({
        'any.required': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
        'string.empty': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
        'string.phoneNumber': VALIDATION_MESSAGE.EMAIL_OR_MOBILE_REQUIRED,
      }),
    }),
  });
}

function notEmailOrMobileRequired(
  schema: ObjectSchema,
  emailRequired?: boolean,
  mobileRequired?: boolean,
  visitorTypesDetailsRequired?: DetailsRequired,
  detailsRequiredForSignIn?: SigninRequirements['detailsRequiredForSignIn'],
) {
  return schema.append({
    email: (
      detailsRequiredForSignIn
        ? emailRequired && (visitorTypesDetailsRequired === 'all' || visitorTypesDetailsRequired === 'email')
        : emailRequired
    )
      ? validEmail().messages({
          'string.empty': VALIDATION_MESSAGE.EMAIL_MISSING,
          'string.email': VALIDATION_MESSAGE.EMAIL_MISSING,
        })
      : optionalField(validEmail()),

    mobile: (
      detailsRequiredForSignIn
        ? mobileRequired && (visitorTypesDetailsRequired === 'all' || visitorTypesDetailsRequired === 'mobile')
        : mobileRequired
    )
      ? mobileSchema().messages({
          'string.empty': VALIDATION_MESSAGE.MOBILE_MISSING,
          'string.phoneNumber': VALIDATION_MESSAGE.MOBILE_INCORRECT,
        })
      : optionalField(mobileSchema()),
  });
}

// required or not, true means it is required, false means it is optional
export default function generateVisitorDetailsFormSchema({
  emailRequired,
  mobileRequired,
  companyRequired,
  emailOrMobileRequired,
  visitorTypesDetailsRequired,
  detailsRequiredForSignIn,
}: {
  emailRequired?: boolean;
  mobileRequired?: boolean;
  emailOrMobileRequired?: boolean;
  companyRequired?: boolean;
  visitorTypesDetailsRequired?: DetailsRequired;
  detailsRequiredForSignIn?: SigninRequirements['detailsRequiredForSignIn'];
}) {
  const _personalInfoSchema = Joi.object<VisitorDetailsForm>().keys({
    firstName: firstNameSchema(),
    lastName: lastNameSchema(),
    company: companyRequired ? companySchema() : optionalField(companySchema()),
    companyId: companyIdSchema(),
    companySource: companySourceSchema(),
    site: singleSelectDropdownOption<SiteOptionWithCountryCode>()
      .append({
        countryCode: Joi.string(),
      })
      .required(),
    visitorType: singleSelectDropdownOptionWithVtRequirements().required(),
    host: Joi.optional().allow(''),
    zone: Joi.optional(),
  });

  if (emailOrMobileRequired) {
    return isEmailOrMobileRequired(_personalInfoSchema);
  } else {
    return notEmailOrMobileRequired(
      _personalInfoSchema,
      emailRequired,
      mobileRequired,
      visitorTypesDetailsRequired,
      detailsRequiredForSignIn,
    );
  }
}
